import type { CSSProperties, JSX } from 'react';
import type { MergedApprovalItem } from '../buildMergedItems';
import { resolveApprovalCellContent } from '../resolveApprovalCellContent';
import { ApprovalCellContent } from '../ApprovalCellContent';
import type { ApprovalLineLayout, ApprovalLineLayoutOptions } from './types';
import type { ApprovalFontStyle } from '../types';
import { boxSpacingToCss, textStyleToCss } from '../../layout';

/**
 * 기본 양식 결재란 레이아웃 — TBM/위험성평가 등.
 *
 * 정본은 양식 결재란 마크업(.approval-section/.approval-cell ...)이고, 이 레이아웃이 화면·PDF 공통의
 * 유일한 양식 결재란이다.
 *
 * - 입력은 buildMergedApprovalItems 결과(MergedApprovalItem[]).
 * - author 단계 칸(isAuthor)에만 onAuthorSignatureClick(작성자 서명 진입점)을 연결한다.
 * - 양식 css(.approval-cell 등)는 `approval-form.css` 하나다(`ApprovalLineDisplay`가 import).
 */

// 칸 너비(80px) - 패딩(4px)에 맞춰 이름 텍스트 크기 동적 계산 (ApprovalFormSection과 동일)
// config.cellWidth가 주어지면 그 너비 기준으로 동적 계산한다.
function getSignerNameFontSize(name: string, cellWidth = 80): string {
  const maxWidth = cellWidth - 8; // 칸 너비 - padding 8px
  const charWidth = 9; // 한글+괄호 혼합 기준 약 9px/자
  const baseSize = 9;
  const neededWidth = name.length * charWidth;
  if (neededWidth <= maxWidth) return `${baseSize}pt`;
  const ratio = maxWidth / neededWidth;
  const adjusted = Math.max(3, Math.floor(baseSize * ratio * 10) / 10);
  return `${adjusted}pt`;
}

/**
 * config 폰트 스타일 → 인라인 CSS. 미지정 필드는 속성 자체를 넣지 않아 CSS 기본 유지.
 * (fontSize는 px로 부여 — config는 px 단위.)
 */
function fontStyleToCss(font: ApprovalFontStyle | undefined): CSSProperties {
  if (!font) return {};
  // 공통 텍스트-런 변환은 textStyleToCss로 일원화. ApprovalFontStyle 고유의 bold→700 하위호환만
  // 여기서 처리해 fontWeight로 정규화(fontWeight 지정 시 그 값 우선, 미지정+bold면 700).
  // (ApprovalFontStyle엔 underline/textIndent 필드가 없어 helper의 해당 분기는 항상 no-op — 동작 동일.)
  return textStyleToCss({
    ...font,
    fontWeight: font.fontWeight ?? (font.bold ? 700 : undefined),
  });
}

function renderApprovalForm(
  items: MergedApprovalItem[],
  opts: ApprovalLineLayoutOptions,
): JSX.Element {
  const { hideFirstAssigneeName = false, onAuthorSignatureClick, offlineSignature, config } = opts;
  const isOffline = !!offlineSignature;

  // 구성 옵션(config) — 미지정 시 전부 기존 동작(회귀 0).
  const showStepLabel = config?.showStepLabel ?? true;
  const showName = config?.showName ?? true;
  const cellWidth = config?.cellWidth;
  const cellBorder = config?.cellBorder;
  const sectionDivider = config?.sectionDivider;
  // 칸 너비 오버라이드(미지정 시 CSS 기본 80 — 인라인 미부여). 칸 외곽 테두리는 per-cell에서 머지.
  const cellStyle: CSSProperties =
    cellWidth !== undefined ? { width: `${cellWidth}px` } : {};

  // 역할 분리: 영역(단계명란/서명란/이름란)은 배경(fill)만, 테두리는 칸 외곽(cellBorder)·
  // 영역 구분선(sectionDivider)이 일원 담당. RectStyle의 border/borderSides는 무시.
  const fillStyle = (rs?: { fill?: string }): CSSProperties =>
    rs?.fill !== undefined ? { backgroundColor: rs.fill } : {};

  // 표시 영역 순서(위→아래): 단계명란(showStepLabel) → 서명란(항상) → 이름란(showName).
  const displayedAreas: ('stepLabel' | 'signature' | 'name')[] = [
    ...(showStepLabel ? (['stepLabel'] as const) : []),
    'signature' as const,
    ...(showName ? (['name'] as const) : []),
  ];
  const lastArea = displayedAreas[displayedAreas.length - 1];
  // sectionDivider 지정 시: 마지막 표시 영역 제외 borderBottom=구분선, 마지막 영역=none(칸 외곽이 담당).
  // 미지정 시 inline 미부여 → CSS 기본(border-bottom) 유지(회귀 0).
  const dividerFor = (area: 'stepLabel' | 'signature' | 'name'): CSSProperties =>
    sectionDivider === undefined
      ? {}
      : { borderBottom: area === lastArea ? 'none' : sectionDivider };

  // 각 영역 높이/폰트/패딩 오버라이드(미지정 시 인라인 미부여 → CSS 기본).
  const labelStyle: CSSProperties = {
    ...fontStyleToCss(config?.stepLabelFont),
    ...(config?.stepLabelHeight !== undefined ? { height: `${config.stepLabelHeight}px` } : {}),
    ...(config?.stepLabelPadding !== undefined ? { padding: boxSpacingToCss(config.stepLabelPadding) } : {}),
    ...fillStyle(config?.stepLabelRect),
    ...dividerFor('stepLabel'),
  };
  const signatureStyle: CSSProperties = {
    ...(config?.signatureHeight !== undefined ? { height: `${config.signatureHeight}px` } : {}),
    ...(config?.signaturePadding !== undefined ? { padding: boxSpacingToCss(config.signaturePadding) } : {}),
    ...fillStyle(config?.signatureRect),
    ...dividerFor('signature'),
  };
  const nameFontCss = fontStyleToCss(config?.nameFont);
  const nameHeightCss: CSSProperties =
    config?.nameHeight !== undefined ? { height: `${config.nameHeight}px` } : {};
  const namePaddingCss: CSSProperties =
    config?.namePadding !== undefined ? { padding: boxSpacingToCss(config.namePadding) } : {};

  return (
    <section className="approval-section">
      <div className="approval-table">
        <div className="approval-group">
          <div className="approval-cells">
            {items.map((item, index) => {
              const cell = resolveApprovalCellContent(item, isOffline);
              // author 단계 칸 — 작성자 서명 진입점 (display registry는 author만 클릭 대상)
              const onClick = item.isAuthor ? onAuthorSignatureClick : undefined;
              const displayName =
                hideFirstAssigneeName && index === 0 ? '' : item.name || '-';

              // 시뮬레이션 빈 동적 슬롯(placeholder)은 고정 단계와 시각 구분(점선/옅은 배경)
              const baseCellClass = item.placeholder
                ? 'approval-cell approval-cell--placeholder'
                : 'approval-cell';
              const cellClass = onClick
                ? `${baseCellClass} cursor-pointer hover:bg-brand-50 transition-colors`
                : baseCellClass;

              // 이름란 폰트: config.nameFont.fontSize 지정 시 그것을 쓰고, 아니면 너비 기반 동적 계산.
              const nameStyle: CSSProperties = {
                fontSize:
                  config?.nameFont?.fontSize !== undefined
                    ? `${config.nameFont.fontSize}px`
                    : getSignerNameFontSize(displayName || '-', cellWidth),
                ...nameFontCss,
                ...nameHeightCss,
                ...namePaddingCss,
                ...fillStyle(config?.nameRect),
                ...dividerFor('name'),
              };

              // 칸 외곽 테두리(cellBorder) — 4변 중 좌변은 첫 칸만 그려 칸 사이 세로선 중복 방지
              // (CSS의 .approval-cell:not(:first-child){border-left:none}과 동일 원리).
              // 미지정 시 inline 미부여 → CSS 기본(.approval-cell) 유지(회귀 0).
              const thisCellStyle: CSSProperties =
                cellBorder !== undefined
                  ? {
                      ...cellStyle,
                      borderTop: cellBorder,
                      borderRight: cellBorder,
                      borderBottom: cellBorder,
                      borderLeft: index === 0 ? cellBorder : 'none',
                    }
                  : cellStyle;

              return (
                <div
                  key={item.key}
                  className={cellClass}
                  style={thisCellStyle}
                  role={onClick ? 'button' : undefined}
                  tabIndex={onClick ? 0 : undefined}
                  aria-label={
                    onClick
                      ? item.signatureData
                        ? '작성자 서명 수정'
                        : '작성자 서명하기'
                      : undefined
                  }
                  title={onClick ? (item.signatureData ? '클릭하여 서명 수정' : '클릭하여 서명하기') : undefined}
                  onClick={onClick ? () => onClick() : undefined}
                  onKeyDown={
                    onClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onClick();
                          }
                        }
                      : undefined
                  }
                >
                  {showStepLabel && (
                    <div className="approval-cell-label" style={labelStyle}>
                      {item.label}
                    </div>
                  )}
                  <div className="signature-area" style={signatureStyle}>
                    <ApprovalCellContent
                      mode="print"
                      kind={cell.kind}
                      signatureData={cell.signatureData}
                      name={cell.name}
                    />
                  </div>
                  {showName && (
                    <div className="signer-name" style={nameStyle}>
                      {displayName}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export const StepperLayout: ApprovalLineLayout = {
  variant: 'box',
  render(items, opts) {
    return renderApprovalForm(items, opts);
  },
};
